import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { TabHeader } from "../../shared_components/tab-header/tab-header.component";
import { BehaviorSubject, Subject, of } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/core/reducers";
import { UpdateGeneralSettings, UpdateTempLocation, selectSystemMapLocation, selectTempLocation, selectLogoFile } from "src/app/core/general-settings/general-settings.store";
import { MapsAPILoader } from "@agm/core";
import { Location } from "@angular/common";
import { zoomForViewport } from "src/app/core/v2/map/viewport-to-zoom";
import { FileUploadService } from "src/app/core/file-upload";
import { HttpEventType } from "@angular/common/http";

@Component({
    selector: 'kt-general-settings',
    templateUrl: 'general-settings.component.html',
    styleUrls: ['./general-settings.component.css']
})
export class GeneralSettingsComponent implements OnInit, OnDestroy {
    // Tabs
    selectedTab = 0
    selectedTabChange: BehaviorSubject<number> = new BehaviorSubject(0)
    selectedTabChange$ = this.selectedTabChange.asObservable()
    tabHeaders: TabHeader[] = [
        {label: 'Maps', disabled: of(false)},
        {label: 'Branding', disabled: of(false)}
    ]
    onTabChange(index: number) {
        this.selectedTab = index
    }

    // Map
    latitude$ = this.store.select(selectTempLocation).pipe(map(location => location.latitude))
    longitude$ = this.store.select(selectTempLocation).pipe(map(location => location.longitude))
    locationState = {
        latitude: null,
        longitude: null
    }
    zoom = 13
    private map: google.maps.Map;
    private mapClickListener: google.maps.MapsEventListener
    @ViewChild('locationSearch', {static: false})
    public searchElementRef: ElementRef

    private _logoFile$ = new BehaviorSubject<{path: string, name: string}>(null)
    logoFile$ = this._logoFile$.asObservable()

    private _onDestroy$: Subject<void> = new Subject<void>()

    constructor(
        private ngZone: NgZone,
        private mapsApiLoader: MapsAPILoader,
        private store: Store<AppState>,
        private location: Location,
        private fileUploadService: FileUploadService
    ) {
    }

    ngOnInit(): void {
        this.store.select(selectTempLocation).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(location => {
            this.locationState = location
        })

        this.store.select(selectLogoFile).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(logo => {
            if (logo == null) {
                return;
            }
            if (logo.name == null || logo.path == null) {
                return;
            }
            this._logoFile$.next(logo)
        })

        this.mapsApiLoader.load().then(() => {
            if (this.searchElementRef === undefined) {
                return;
            }
            const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
                types: ['geocode']
            })
            autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                    const place = autocomplete.getPlace()
                    if (place.geometry === undefined || place.geometry === null) {
                        return;
                    }
                    const latitude = place.geometry.location.lat();
                    const longitude = place.geometry.location.lng();
                    this.zoom = zoomForViewport(place.geometry.viewport)
                    this.store.dispatch(new UpdateTempLocation({latitude, longitude}))
                })
            })
        })
    }

    ngOnDestroy(): void {
        this._onDestroy$.next() 
        this._onDestroy$.complete(),
        this.mapClickListener.remove()
    }

    onMapReady(event: any) {
        this.map = event
        this.mapClickListener = this.map.addListener('rightclick', (e: google.maps.MouseEvent) => {
            this.ngZone.run(() => {
                this.mapRightClick(e)
            })
        })
    }

    onClickUploadFile() {
        const inputElement = document.createElement('input')
        inputElement.type = 'file'
        inputElement.accept = 'application/pdf,image/*'
        inputElement.multiple = false
        inputElement.click()

        inputElement.onchange = () => {
            this._uploadFile(inputElement.files)
        }
    }

    private _uploadFile(files: FileList) {
        const formData = new FormData();
        Array.from(files).forEach((file, i) => {
        formData.append('files[' + i + ']', file, file.name)
        })

        this.fileUploadService.uploadFile(formData).subscribe(value => {
            if (value.type !== HttpEventType.Response) {
                return;
            }
            const body = value.body as any
            this._logoFile$.next({
                name: body.uploaded_files[0].name,
                path: body.uploaded_files[0].path
            })
        })
    }


    onSubmit() {
        this.store.dispatch(new UpdateGeneralSettings({
            item: {location: this.locationState, logo: this._logoFile$.getValue()}
        }))
    }

    back() {
        this.location.back()
    }

    private mapRightClick(event: google.maps.MouseEvent) {
        const latitude = event.latLng.lat();
        const longitude = event.latLng.lng();
        this.store.dispatch(new UpdateTempLocation({latitude, longitude}))
    }
}